"use client";

import { Button } from "@ui/components/button";
import { CheckIcon, CopyIcon, FileTextIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import {
	AI_ASSISTANTS,
	buildAskPrompt,
} from "../lib/build-doc-markdown";

export interface DocsAIActionsProps {
	/** The page rendered as Markdown — used for instant client-side copy. */
	markdown: string;
	/** Absolute URL serving the page as Markdown (linked from the AI prompt). */
	markdownUrl: string;
	/** Page title, used in the prefilled AI prompt. */
	title: string;
}

export function DocsAIActions({
	markdown,
	markdownUrl,
	title,
}: DocsAIActionsProps) {
	const t = useTranslations("documentation.aiActions");
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(markdown);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy markdown:", err);
		}
	};

	const prompt = buildAskPrompt({ title, markdownUrl });

	return (
		<div className="not-prose mb-8 flex flex-wrap items-center gap-2">
			<Button variant="outline" size="sm" onClick={handleCopy}>
				{copied ? (
					<CheckIcon className="size-4" />
				) : (
					<CopyIcon className="size-4" />
				)}
				{copied ? t("copied") : t("copy")}
			</Button>

			<Button variant="outline" size="sm" asChild>
				<a href={markdownUrl} target="_blank" rel="noopener noreferrer">
					<FileTextIcon className="size-4" />
					{t("viewMarkdown")}
				</a>
			</Button>

			<span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden />

			{AI_ASSISTANTS.map((assistant) => (
				<Button key={assistant.id} variant="ghost" size="sm" asChild>
					<a
						href={assistant.urlFor(prompt)}
						target="_blank"
						rel="noopener noreferrer"
					>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img
							src={assistant.logo}
							alt=""
							aria-hidden
							className="mr-1.5 size-4"
						/>
						{t("ask", { tool: assistant.label })}
					</a>
				</Button>
			))}
		</div>
	);
}
